import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Document, Page } from "react-pdf";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import { storage, toastOptionsAtom, userAtom } from "../../atoms";
import {
  useAcceptTermsMutation,
  useFetchTermsQuery,
  useLogoffMutation,
} from "../../api";
import {
  SESSION_STORAGE_USER_KEY,
  TERMS_SCROLL_END_THRESHOLD,
  TERMS_TITLES,
} from "../../constants";
import { LoginRoute } from "../../routes";
import { User } from "../../types";
import { getApiErrorMessage, theme } from "../../utils";
import { Button, ConfirmationModal } from "../common";

type TermsAcceptanceDialogProps = {
  open: boolean;
  user: User;
};

const contentFrameSx = {
  flex: 1,
  minHeight: 200,
  border: `1px solid ${theme.palette.payment.grayBorder}`,
  borderRadius: 2,
  backgroundColor: theme.palette.payment.muted,
};

export const TermsAcceptanceDialog: React.FC<TermsAcceptanceDialogProps> = ({
  open,
  user,
}) => {
  const [, setUser] = useAtom(userAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);
  const [termsFileLink, setTermsFileLink] = useState<string>();
  const [pageWidth, setPageWidth] = useState<number>();
  const [numPages, setNumPages] = useState(0);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);

  const title = TERMS_TITLES[user.roleCode] ?? "Terms of Service";

  const fetchTermsQuery = useFetchTermsQuery(user.orgId, user.id, {
    enabled: open,
  });
  const acceptTermsMutation = useAcceptTermsMutation();
  const {
    mutate: logoff,
    isSuccess: isLoggedOff,
    isError: isLogoffError,
  } = useLogoffMutation();

  useEffect(() => {
    if (!fetchTermsQuery.data) {
      setTermsFileLink(undefined);
      return;
    }

    const linkToPdf = URL.createObjectURL(
      new Blob([new Uint8Array(fetchTermsQuery.data)], {
        type: "application/pdf",
      }),
    );
    setTermsFileLink(linkToPdf);

    return () => URL.revokeObjectURL(linkToPdf);
  }, [fetchTermsQuery.data]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) =>
      setPageWidth(entry.contentRect.width),
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [termsFileLink]);

  useEffect(() => {
    if (isLoggedOff || isLogoffError) {
      setUser(null);
      storage.removeItem(SESSION_STORAGE_USER_KEY);
      navigate(LoginRoute.path);
    }
  }, [isLoggedOff, isLogoffError]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || hasReachedEnd || !numPages) return;
    if (
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - TERMS_SCROLL_END_THRESHOLD
    ) {
      setHasReachedEnd(true);
    }
  };

  const checkHasReachedEnd = () => {
    const el = scrollRef.current;
    if (!el || hasReachedEnd) return;
    if (el.scrollHeight - el.clientHeight <= TERMS_SCROLL_END_THRESHOLD) {
      setHasReachedEnd(true);
    }
  };

  useEffect(() => {
    if (numPages > 0) {
      const timer = setTimeout(checkHasReachedEnd, 150);
      return () => clearTimeout(timer);
    }
  }, [numPages, pageWidth]);

  const handleAccept = () => {
    acceptTermsMutation.mutate(
      { orgId: user.orgId, userId: user.id },
      {
        onSuccess: (acceptance) => setUser({ ...user, ...acceptance }),
        onError: (error) =>
          setToastOptions({
            open: true,
            message: getApiErrorMessage(error),
            severity: "error",
          }),
      },
    );
  };

  const handleDecline = () => {
    setDeclineModalOpen(false);
    logoff({ userName: user.userName });
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        disableEscapeKeyDown
        onClose={() => {}}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(255, 255, 255, 0.35)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            height: isMobile ? "100%" : "90vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.5,
              color: "#B45309",
              textTransform: "uppercase",
            }}
          >
            Action required
          </Typography>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 700, color: theme.palette.secondary.main }}
          >
            {title}
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            Hi {user.firstName}, please read and accept the terms to continue to
            your dashboard.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", pb: 0 }}>
          {!termsFileLink ? (
            <Box
              sx={{
                ...contentFrameSx,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              {fetchTermsQuery.isError ? (
                <>
                  <Typography variant="body2">
                    We couldn't load the Terms of Service.
                  </Typography>
                  <Button
                    variantType="secondary"
                    label="Try again"
                    size="small"
                    onClick={() => fetchTermsQuery.refetch()}
                  />
                </>
              ) : (
                <CircularProgress size={32} />
              )}
            </Box>
          ) : (
            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              sx={{
                ...contentFrameSx,
                overflowY: "auto",
                "& .react-pdf__Page": {
                  borderBottom: `1px solid ${theme.palette.payment.grayBorder}`,
                },
              }}
            >
              <Document
                file={termsFileLink}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                }
              >
                {Array.from({ length: numPages }, (_, index) => (
                  <Page
                    key={index}
                    pageNumber={index + 1}
                    width={pageWidth}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                ))}
              </Document>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1,
              color: hasReachedEnd
                ? theme.palette.payment.greenText
                : theme.palette.payment.mutedFg,
            }}
          >
            {hasReachedEnd ? (
              <CheckCircleOutlineIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
            <Typography variant="caption">
              {hasReachedEnd
                ? "You've reached the end"
                : "Scroll to the end to continue"}
            </Typography>
          </Box>

          <FormControlLabel
            sx={{ mt: 1.5, alignItems: "flex-start" }}
            control={
              <Checkbox
                checked={agreed}
                disabled={!hasReachedEnd || acceptTermsMutation.isLoading}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{ pt: 0.25 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                I have read and agree to the Sprayzapp {title}.
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions
          sx={{
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontStyle: "italic", color: theme.palette.payment.mutedFg }}
          >
            Your acceptance will be recorded for audit purposes.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: 1.5,
              ml: "0 !important",
            }}
          >
            <Button
              variantType="secondary"
              label="Decline & Log out"
              disabled={acceptTermsMutation.isLoading}
              onClick={() => setDeclineModalOpen(true)}
              sx={{ px: 3 }}
            />
            <Button
              label={
                acceptTermsMutation.isLoading
                  ? "Accepting..."
                  : "Accept & Continue"
              }
              disabled={!agreed || acceptTermsMutation.isLoading}
              onClick={handleAccept}
              sx={{ px: 3 }}
            />
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={declineModalOpen}
        onClose={() => setDeclineModalOpen(false)}
        onConfirm={handleDecline}
        icon={
          <WarningAmberOutlinedIcon
            sx={{ fontSize: 48, color: theme.palette.error.main }}
          />
        }
        title="Decline Terms?"
        message="You won't be able to access Sprayzapp without accepting the Terms of Service. You will be logged out."
      />
    </>
  );
};
